/**
 * Life4Earth V2 — Mini-Project API client (DEV-810 / DEV-901 stage 3a).
 *
 * Wrappers for the Mini-Project backend endpoints. Stage 3a covers the
 * core Mini-Project + Playbook endpoints needed for the page shell.
 * Sub-entity wrappers (Goals, Tasks, Team, BOM, Enablers) land
 * in stages 3b–3d as those surfaces get built.
 *
 * All calls go through apiFetch which handles base URL, Bearer JWT, and
 * X-User-Guid fallback for dev. Errors throw ApiError.
 */

import { apiFetch, ApiError } from "./client";

// ════════════════════════════════════════════════════════════════
// Mutation broadcast (Task #221)
// ════════════════════════════════════════════════════════════════
//
// MP mutation paths (status post, status edit, goal upsert, lifecycle
// transition, metadata update, etc.) need to wake any portfolio-level
// consumer that's caching MP data. Unlike the FP path (which routes
// through apiMutateCommunity → invalidateAndRefetch on a shared
// queryCache key), the MP hooks (useApiMiniProjects,
// useApiMiniProjectPortfolioSignals) are local-state fetches with no
// shared cache subscription. So we use a CustomEvent the way several
// other surfaces in the app already do (l4e_miniproject_team_persisted,
// l4e_miniproject_roles_changed, etc.).
//
// Contract: any function that successfully writes to an MP-scoped
// resource MUST dispatch EVT_MINI_PROJECT_MUTATED with the affected
// miniProjectGuid in detail. Both hooks listen and refetch.

export const EVT_MINI_PROJECT_MUTATED = "l4e_miniproject_mutated";

export function dispatchMiniProjectMutated(miniProjectGuid: string): void {
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent(EVT_MINI_PROJECT_MUTATED, {
          detail: { miniProjectGuid },
        }),
      );
    }
  } catch {
    // CustomEvent constructor unavailable (SSR, locked-down env). Safe
    // to ignore — the caller's mutation succeeded; the broadcast is
    // best-effort UI refresh, not load-bearing data persistence.
  }
}

// ════════════════════════════════════════════════════════════════
// Mini-Project core (live instance)
// ════════════════════════════════════════════════════════════════

export interface MiniProject {
  miniProjectGuid: string;
  title: string;
  description?: string | null;
  /** Card blurb shown on project cards + at the top of the About tab.
   *  Migration 166. Separate from the 5 narrative About blocks. */
  shortDescription?: string | null;
  playbookGuid?: string | null;
  playbookVersion?: number | null;
  ownerUserGuid: string;
  ownerCommunityGuid: string;
  communityMetadata: string;
  /**
   * Task #218 / migration 185. Selected metadata schema for this MP.
   * References collection_schemas.schema_guid (soft ref). Drives the
   * fields rendered on the Metadata tab in MiniProjectSettingsModal.
   * NULL means no schema selected.
   */
  metadataSchemaGuid?: string | null;
  status: "draft" | "active" | "complete" | "expired";
  startDate?: string | null;
  endDate?: string | null;
  activatedAt?: string | null;
  completedAt?: string | null;
  archivedAt?: string | null;
  /**
   * DEV-903 Phase K (migration 224). TRUE = playbook master project —
   * the editable authoring artifact behind a playbook, never a real
   * running project. API list endpoints exclude masters by default
   * (includeMasters=true to opt in, e.g. the Structures tab). Frontend
   * consumers that assemble mini-project collections from other sources
   * must apply excludeMasters() from src/Data/miniProjectMasterFilter.ts.
   */
  isMaster?: boolean;
  heroImageUrl?: string | null;
  heroOriginalUrl?: string | null;
  /** Card-specific hero (migration 191). Overrides heroImageUrl on
   *  project cards when set; cards fall back to heroImageUrl when null. */
  cardImageUrl?: string | null;
  categoryKey?: string | null;
  // About content moved to project_content_sections (migration 167);
  // fetch via /api/mini-projects/{guid}/sections.
  locationGeographyGuid?: string | null;
  locationText?: string | null;
  locationLat?: number | null;
  locationLon?: number | null;
  lastDataUpdateAt?: string | null;
  visibility: "team_only" | "community" | "public";
  /** Independent visibility for the Progress tab. Capped at `visibility`. */
  progressVisibility: "team_only" | "community" | "public";
  /** Backend-computed: can the current user read the Progress tab on this MP?
   *  Populated by the GetOne endpoint; null/undefined elsewhere. */
  canReadProgress?: boolean | null;
  /** Verification workflow status (migration 166). Drafting | Under Review |
   *  Peer Reviewed | Published | Archived. Independent of lifecycle. */
  contentStatus?: string | null;
  /** User-facing project status (migration 168). Proposed | Planned |
   *  In Progress | Abandoned | Completed. Separate from lifecycle. */
  projectStatus?: string | null;
  /** Planned budget (migration 166). Pair with plannedBudgetCurrency. */
  plannedBudgetAmount?: number | null;
  plannedBudgetCurrency?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMiniProjectRequest {
  title: string;
  description?: string;
  ownerCommunityGuid: string;
  communityMetadata?: string;
  startDate?: string;
  endDate?: string;
  heroImageUrl?: string;
  heroOriginalUrl?: string;
  categoryKey?: string;
  /** Planned ("target") budget. Pair amount with currency; null both if unplanned. */
  plannedBudgetAmount?: number | null;
  plannedBudgetCurrency?: string | null;
  visibility?: "team_only" | "community" | "public";
}

export interface InstantiateMiniProjectFromPlaybookRequest {
  playbookGuid: string;
  title?: string;
  ownerCommunityGuid: string;
  communityMetadata?: string;
  startDate?: string;
  endDate?: string;
  visibility?: "team_only" | "community" | "public";
  // ── DEV-903 Phase I (DEV-1259): localization at instantiation ──
  // Map pin (optional; the stepper UI drives these).
  locationGeographyGuid?: string;
  locationText?: string;
  locationLat?: number;
  locationLon?: number;
  /** Optional hero override — the instantiator may replace the
   *  template's default hero (falls back to the playbook's). */
  heroImageUrl?: string;
  /** Skip 2026-06-07: the playbook's collection is mandatory, but the
   *  instantiator may opt their instance out of enrollment (default
   *  false = enrolled). Opt back in later via collection membership. */
  optOutOfCollection?: boolean;
  /** DEV-1282 — instantiator-confirmed planned budget for this run. */
  plannedBudgetAmount?: number | null;
  plannedBudgetCurrency?: string | null;
  /** DEV-1284 (Step 2) — per-goal target overrides, keyed by the published
   *  mission goal's key. Omitted goals keep the playbook default target. */
  goalTargetOverrides?: { key: string; targetGoal: number | null }[];
}

export interface UpdateMiniProjectRequest {
  title?: string;
  description?: string;
  /** Card blurb (migration 166). Shown on cards + top of About tab. */
  shortDescription?: string;
  /** Optional owner transfer. Server validates caller has content_creator/admin/owner on the new community. */
  ownerCommunityGuid?: string;
  communityMetadata?: string;
  /**
   * Task #218 / migration 185. Selected metadata schema GUID. Pass
   * "00000000-0000-0000-0000-000000000000" (Guid.Empty) to clear.
   * Omit/undefined means "no change".
   */
  metadataSchemaGuid?: string;
  startDate?: string;
  endDate?: string;
  heroImageUrl?: string;
  heroOriginalUrl?: string;
  /** Card-specific hero (migration 191). Lives on its own column so
   *  card crops don't overwrite the main hero. Pass empty string to clear. */
  cardImageUrl?: string;
  categoryKey?: string;
  /** Verification workflow status (migration 166).
   *  Values: Drafting | Under Review | Peer Reviewed | Published | Archived.
   *  Pass empty string to clear. */
  contentStatus?: string;
  /** User-facing project status (migration 168).
   *  Values: Proposed | Planned | In Progress | Abandoned | Completed.
   *  Pass empty string to clear. */
  projectStatus?: string;
  plannedBudgetAmount?: number;
  /** ISO 4217 3-letter code. Pass empty string to clear. */
  plannedBudgetCurrency?: string;
  // About content moved to project_content_sections (migration 167).
  // Use the section CRUD endpoints at /api/mini-projects/{guid}/sections
  // (see useApiProjectSections with entityType='mini_project').
  locationGeographyGuid?: string;
  locationText?: string;
  locationLat?: number;
  locationLon?: number;
  visibility?: "team_only" | "community" | "public";
  progressVisibility?: "team_only" | "community" | "public";
}

export interface TransitionMiniProjectRequest {
  /** DEV-1271: "draft" added for the un-start (active→draft) back-out;
   *  "active" also serves Reopen (complete/expired→active). */
  newStatus: "active" | "complete" | "expired" | "draft";
}

export interface MiniProjectListFilters {
  ownerCommunityGuid?: string;
  status?: string;
  categoryKey?: string;
  visibility?: string;
  includeArchived?: boolean;
}

function buildQuery(filters: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === "") continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length > 0 ? `?${parts.join("&")}` : "";
}

export const fetchMiniProjects = (filters: MiniProjectListFilters = {}) =>
  // Cast: buildQuery accepts Record<string, unknown>, but our typed
  // interface filters don't auto-satisfy that without an index signature
  // under strict TS settings. Cast is safe — buildQuery only enumerates
  // own-property keys.
  apiFetch<MiniProject[]>(`/api/mini-projects${buildQuery(filters as Record<string, unknown>)}`);

// ── DEV-1116 Phase 1b — Portfolio signals (bulk) ─────────────────

/** Slim per-MP latest-status payload the portfolio aggregator feeds
 *  into computeMiniProjectKpiStatus(latestStatus). Only the fields that
 *  the Schedule classifier and the Budget dot logic actually read. */
export interface MiniProjectPortfolioLatestStatus {
  plannedPct: number;
  actualPct: number;
  reportingDate: string;
  /** Migration 190 — Lead's qualitative budget judgement at the latest
   *  status. NULL when not yet assessed. Drives the portfolio Budget
   *  KPI dot color via computeMiniProjectKpiStatus. */
  budgetAssessment?: string | null;
}

/** Slim per-goal payload for Mission scoring on the dot. Mirrors the
 *  shape that computeMiniProjectKpiStatus reads off goals[]: the
 *  includedInRollup flag and the latest qualitative assessment. */
export interface MiniProjectPortfolioGoal {
  goalGuid: string;
  includedInRollup: boolean;
  latestAssessment: string | null;
  /** Bug A (portfolio trend arrows) — the goal's assessment as of the
   *  previous filed status's reporting date. Null when no previous
   *  status exists or no point predates it. Feeds the Mission trend
   *  comparison (computeMpKpiTrends) for the grid's MP rows. */
  previousAssessment: string | null;
}

export interface MiniProjectPortfolioSignalsRow {
  miniProjectGuid: string;
  latestStatus: MiniProjectPortfolioLatestStatus | null;
  /** Bug A — the 2nd-newest filed status (same ordering as latestStatus).
   *  Null when fewer than 2 statuses exist (first-status case → all
   *  trends null → circles). Feeds the Schedule/Budget trend comparison
   *  (computeMpKpiTrends) for the grid's MP rows. */
  previousStatus: MiniProjectPortfolioLatestStatus | null;
  goals: MiniProjectPortfolioGoal[];
}

/**
 * Bulk fetch of the slim inputs (latest status + per-goal latest
 * assessment) the portfolio Data aggregator needs to color the
 * Schedule + Mission dots on each MP row. Visibility is enforced
 * server-side by CanReadMiniProject; archived MPs are excluded.
 */
export const fetchMiniProjectPortfolioSignals = () =>
  apiFetch<MiniProjectPortfolioSignalsRow[]>("/api/mini-projects/portfolio-signals");

/**
 * Fetch one Mini-Project by guid. Returns null on 404 (rather than throwing)
 * so the dispatcher can cleanly detect "not a Mini-Project" without try/catch.
 */
export async function fetchMiniProject(miniProjectGuid: string): Promise<MiniProject | null> {
  try {
    return await apiFetch<MiniProject>(`/api/mini-projects/${miniProjectGuid}`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export const createMiniProject = (req: CreateMiniProjectRequest) =>
  apiFetch<MiniProject>("/api/mini-projects", {
    method: "POST",
    body: JSON.stringify(req),
  });

export const instantiateMiniProjectFromPlaybook = (req: InstantiateMiniProjectFromPlaybookRequest) =>
  apiFetch<MiniProject>("/api/mini-projects/from-playbook", {
    method: "POST",
    body: JSON.stringify(req),
  });

// DEV-1271 fix: these two mutations previously did NOT broadcast
// EVT_MINI_PROJECT_MUTATED, so renames and lifecycle transitions left the
// App.tsx aggregator (left-pane cards + KPIGridTable) stale until a full
// reload. Every other MP mutation dispatches on success; these now do too.
export async function updateMiniProject(
  guid: string,
  req: UpdateMiniProjectRequest,
): Promise<MiniProject> {
  const result = await apiFetch<MiniProject>(`/api/mini-projects/${guid}`, {
    method: "PUT",
    body: JSON.stringify(req),
  });
  dispatchMiniProjectMutated(guid);
  return result;
}

export async function transitionMiniProject(
  guid: string,
  req: TransitionMiniProjectRequest,
): Promise<MiniProject> {
  const result = await apiFetch<MiniProject>(`/api/mini-projects/${guid}/transition`, {
    method: "POST",
    body: JSON.stringify(req),
  });
  dispatchMiniProjectMutated(guid);
  return result;
}

/**
 * POLICY_PROJECT_OWNERSHIP_TRANSFER.md Phase 2/3 — transfer ownership to
 * another organization community. The caller (Lead) must hold community:admin
 * on the destination (auto-commit) or community:create_projects (Phase 3
 * approval — currently returns 403 approval_required).
 *
 * On 403, the thrown ApiError carries body { error, message } so callers
 * can branch on `err.body.error === 'approval_required'` to show a clear
 * message rather than a generic failure.
 */
export const transferMiniProjectOwnership = (guid: string, toCommunityGuid: string) =>
  apiFetch<MiniProject>(`/api/mini-projects/${guid}/transfer-ownership`, {
    method: "POST",
    body: JSON.stringify({ toCommunityGuid }),
  });

/**
 * Archive (hide from default lists). Status unaffected.
 * Reversible via unarchiveMiniProject. Refactor 2026-05-19.
 *
 * DEV-1210 — Broadcasts MiniProject mutation event after success so consumers
 * (useApiMiniProjects, portfolio aggregations) refetch. Without this, the
 * frontend cache held the pre-archive snapshot and the row stayed visible
 * (or after restore, stayed invisible) until a full page reload.
 */
export const archiveMiniProject = async (guid: string): Promise<MiniProject> => {
  const result = await apiFetch<MiniProject>(`/api/mini-projects/${guid}/archive`, { method: "POST" });
  dispatchMiniProjectMutated(guid);
  return result;
};

/** Unarchive (restore to default lists). Status unaffected. DEV-1210 — broadcasts mutation event. */
export const unarchiveMiniProject = async (guid: string): Promise<MiniProject> => {
  const result = await apiFetch<MiniProject>(`/api/mini-projects/${guid}/unarchive`, { method: "POST" });
  dispatchMiniProjectMutated(guid);
  return result;
};

/**
 * Admin "Project Cleanup" hard-delete — permanently removes a mini-project and
 * all its child data. System-admin only (enforced server-side). Irreversible.
 */
export const hardDeleteMiniProject = (guid: string): Promise<void> =>
  apiFetch<void>(`/api/mini-projects/${guid}/hard`, { method: "DELETE" });

// ════════════════════════════════════════════════════════════════
// Mini-Project Playbooks
// ════════════════════════════════════════════════════════════════

export interface MiniProjectPlaybook {
  playbookGuid: string;
  ownerCommunityGuid: string;
  ownerUserGuid: string;
  title: string;
  description?: string | null;
  categoryKey?: string | null;
  tags: string[];
  scope: "private" | "cross_org_visible" | "public_open";
  heroImageUrl?: string | null;
  currentVersion: number;
  publishedContent: string;
  isActive: boolean;
  /**
   * DEV-903 Phase T (DEV-1258). The playbook's MASTER project — its
   * editable authoring surface (clone-as-master model). Structure is
   * snapshotted from the master on publish; the master's content
   * sections are the live knowledge baselines. NULL = legacy
   * masterless playbook (JSONB-draft authoring).
   */
  masterMiniProjectGuid?: string | null;
  /**
   * DEV-903 Phase T. Owner's portfolio collection; instances auto-enroll
   * (Phase I). One collection per playbook, persistent across versions —
   * the portfolio outcome rollup aggregates across it.
   */
  collectionGuid?: string | null;
  /**
   * Migration 225. Provenance: the completed mini-project this playbook
   * was templatized from. Suppresses the post-completion "Create a
   * Playbook" re-entry affordances and powers the duplicate guard.
   * NULL = legacy/manually-authored playbook.
   */
  sourceMiniProjectGuid?: string | null;
  /** DEV-1278 catalog-card summary fields (populated from the master at
   *  create and re-synced on publish). All optional. */
  plannedBudgetAmount?: number | null;
  plannedBudgetCurrency?: string | null;
  typicalDurationDays?: number | null;
  minVolunteers?: number | null;
  primaryOutcome?: string | null;
  goalCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface MiniProjectPlaybookVersion {
  playbookGuid: string;
  version: number;
  publishedContent: string;
  publishedByUserGuid: string;
  publishedAt: string;
  changeNotes?: string | null;
}

export interface CreateMiniProjectPlaybookRequest {
  ownerCommunityGuid: string;
  title: string;
  description?: string;
  categoryKey?: string;
  tags?: string[];
  scope?: "private" | "cross_org_visible" | "public_open";
  heroImageUrl?: string;
  publishedContent?: string;
}

export interface UpdateMiniProjectPlaybookRequest {
  title?: string;
  description?: string;
  categoryKey?: string;
  tags?: string[];
  scope?: "private" | "cross_org_visible" | "public_open";
  heroImageUrl?: string;
  isActive?: boolean;
}

export interface PublishMiniProjectPlaybookRequest {
  /** Ignored for master-backed playbooks — the snapshot is built
   *  server-side from the master's live structure (DEV-1258). */
  publishedContent: string;
  changeNotes?: string;
}

/**
 * DEV-903 Phase T (DEV-1258). Create a playbook from an existing
 * mini-project via clone-as-master. The source project is never
 * modified. Cross-org-derived sources are rejected (403); same-org
 * derived sources get 409 directing to the original playbook's master.
 */
export interface CreatePlaybookFromMiniProjectRequest {
  ownerCommunityGuid: string;
  title?: string;
  description?: string;
  categoryKey?: string;
  tags?: string[];
  scope?: "private" | "cross_org_visible" | "public_open";
  /** Link an existing collection… */
  collectionGuid?: string;
  /** …or create a new one (requires collectionSchemaGuid). */
  collectionName?: string;
  collectionSchemaGuid?: string;
  changeNotes?: string;
}

export const createPlaybookFromMiniProject = (
  miniProjectGuid: string,
  req: CreatePlaybookFromMiniProjectRequest,
) =>
  apiFetch<MiniProjectPlaybook>(
    `/api/mini-project-playbooks/from-mini-project/${miniProjectGuid}`,
    { method: "POST", body: JSON.stringify(req) },
  );

/** DEV-1266: pre-flight templatize eligibility so the stepper can tell the
 *  user up front. reason ∈ ok | not_found | is_master | not_complete |
 *  already_exists | same_community | cross_org. */
export interface TemplatizeEligibility {
  eligible: boolean;
  reason: string;
  existingPlaybookGuid?: string | null;
  existingMasterGuid?: string | null;
  title?: string | null;
}

export const fetchTemplatizeEligibility = (miniProjectGuid: string) =>
  apiFetch<TemplatizeEligibility>(
    `/api/mini-project-playbooks/from-mini-project/${miniProjectGuid}/eligibility`,
  );

export interface MiniProjectPlaybookListFilters {
  scope?: string;
  ownerCommunityGuid?: string;
  categoryKey?: string;
  tag?: string;
  includeInactive?: boolean;
}

export const fetchMiniProjectPlaybooks = (filters: MiniProjectPlaybookListFilters = {}) =>
  // Cast: same reason as fetchMiniProjects above — interface filters
  // don't auto-satisfy Record<string, unknown> under strict TS.
  apiFetch<MiniProjectPlaybook[]>(`/api/mini-project-playbooks${buildQuery(filters as Record<string, unknown>)}`);

export const fetchMiniProjectPlaybook = (playbookGuid: string) =>
  apiFetch<MiniProjectPlaybook>(`/api/mini-project-playbooks/${playbookGuid}`);

/** DEV-1267 — resolve the playbook a master mini-project belongs to (for the
 *  master's Publish control + version history). 404 if none/not a master. */
export const fetchMiniProjectPlaybookByMaster = (masterMiniProjectGuid: string) =>
  apiFetch<MiniProjectPlaybook>(`/api/mini-project-playbooks/from-master/${masterMiniProjectGuid}`);

export const fetchMiniProjectPlaybookVersions = (playbookGuid: string) =>
  apiFetch<MiniProjectPlaybookVersion[]>(`/api/mini-project-playbooks/${playbookGuid}/versions`);

export const fetchMiniProjectPlaybookVersion = (playbookGuid: string, version: number) =>
  apiFetch<MiniProjectPlaybookVersion>(`/api/mini-project-playbooks/${playbookGuid}/versions/${version}`);

export const createMiniProjectPlaybook = (req: CreateMiniProjectPlaybookRequest) =>
  apiFetch<MiniProjectPlaybook>("/api/mini-project-playbooks", {
    method: "POST",
    body: JSON.stringify(req),
  });

export const updateMiniProjectPlaybook = (playbookGuid: string, req: UpdateMiniProjectPlaybookRequest) =>
  apiFetch<MiniProjectPlaybook>(`/api/mini-project-playbooks/${playbookGuid}`, {
    method: "PUT",
    body: JSON.stringify(req),
  });

export const publishMiniProjectPlaybook = (playbookGuid: string, req: PublishMiniProjectPlaybookRequest) =>
  apiFetch<MiniProjectPlaybook>(`/api/mini-project-playbooks/${playbookGuid}/publish`, {
    method: "POST",
    body: JSON.stringify(req),
  });

export const softDeleteMiniProjectPlaybook = (playbookGuid: string) =>
  apiFetch<void>(`/api/mini-project-playbooks/${playbookGuid}`, {
    method: "DELETE",
  });

/** DEV-1277 — PERMANENT delete (irreversible). Removes the playbook, its
 *  versions, and the master project. The server returns 409 (ApiError with
 *  status 409, body.instanceCount/message) when live instances exist. */
export const hardDeleteMiniProjectPlaybook = (playbookGuid: string) =>
  apiFetch<void>(`/api/mini-project-playbooks/${playbookGuid}/permanent`, {
    method: "DELETE",
  });

// ════════════════════════════════════════════════════════════════
// Sub-entities (Goals, Tasks, Team, BOM, Enablers) — stage 3b.
// Media uses the platform's generic <ProgressGallery entityType="mini_project" /> —
// see MediaLinksController. migration 164 retired the standalone mini_project_media table.
// ════════════════════════════════════════════════════════════════

export interface MiniProjectGoal {
  goalGuid: string;
  miniProjectGuid: string;
  playbookGoalKey?: string | null;
  nameKey: string;
  primitive: "count" | "percentage" | "checklist" | "currency";
  unitKey?: string | null;
  targetValue?: number | null;
  currentValue: number;
  required: boolean;
  checklistItems?: string | null;
  position: number;
  currencyCode?: string | null;
  lastUpdatedAt: string;
  lastUpdatedByUserGuid?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MiniProjectTask {
  taskGuid: string;
  miniProjectGuid: string;
  playbookTaskKey?: string | null;
  nameKey?: string | null;
  nameText?: string | null;
  description?: string | null;
  status: "not_started" | "in_progress" | "done";
  ownerUserGuid?: string | null;
  /** DEV-1123 (A). Polymorphic assignee — points to a project role slot
   *  (mini_project_roles.role_guid). Mutually exclusive with
   *  ownerUserGuid (XOR check on the column). When both are null, the
   *  display falls back to the project Lead per Skip's "unassigned
   *  belongs to the lead" rule. */
  assignedRoleGuid?: string | null;
  dueDate?: string | null;
  notes?: string | null;
  /** DEV-1124 Knowledge Flywheel Phase 1. Optional external reference
   *  URL (website / YouTube / etc.) describing how to do this task.
   *  Free-form; null = no link. */
  howToUrl?: string | null;
  evidenceAttachmentKeys: string[];
  instructionSteps?: string | null;
  requiresEvidence: boolean;
  position: number;
  isTemplateTask: boolean;
  /** DEV-1285. Platform-seeded task (warm-start milestone). Mirrors the
   *  field on the miniProjectTasks.ts MiniProjectTask shape. */
  isSystemTask: boolean;
  createdAt: string;
  actualStartAt?: string | null;
  completedAt?: string | null;
  updatedAt: string;
}

/**
 * Mini-Project team membership. Schema collapsed by migration 172
 * (POLICY_TEAM_ROSTERS.md §3): the team table IS the ACL — role='lead' is
 * the project's Lead authority anchor. Co-Lead deferred to v1.1.
 *
 * The legacy freeform `roleKey` field was retired. `role` is a strict enum;
 * `displayTitle` is the freeform position label ("Project Lead", "Field
 * Coordinator", etc.) that defaults to the capitalized role value at
 * display time when null.
 */
export interface MiniProjectTeamMember {
  teamMemberGuid: string;
  miniProjectGuid: string;
  userGuid: string;
  /** 'lead' | 'member'. */
  role: string;
  /** Freeform position label. Server defaults to capitalized role
   *  ("Lead" / "Member") when unset. */
  displayTitle: string;
  /** Display priority. Lower = higher in the list. */
  sortOrder: number;
  /** When true, hide from public team display. Admins still see in Settings. */
  isHidden: boolean;
  /** Who added this member. Null for legacy rows. */
  invitedByUserGuid?: string | null;
  joinedAt: string;
  leftAt?: string | null;
}

/** Payload for adding a single team member via POST. Role MUST be
 *  'member' — Lead promotion goes through transferMiniProjectLead. */
export interface AddMiniProjectTeamMemberRequest {
  userGuid: string;
  role?: "lead" | "member";
  displayTitle?: string;
  sortOrder?: number;
  isHidden?: boolean;
}

/** Payload for updating a single team member's metadata. */
export interface UpdateMiniProjectTeamMemberRequest {
  displayTitle?: string;
  sortOrder?: number;
  isHidden?: boolean;
}

/** Item in a BulkSave roster. Omit teamMemberGuid for new rows. */
export interface BulkMiniProjectTeamMember {
  teamMemberGuid?: string | null;
  userGuid: string;
  role: "lead" | "member";
  displayTitle: string;
  sortOrder: number;
  isHidden: boolean;
}

export interface MiniProjectBomItem {
  bomItemGuid: string;
  miniProjectGuid: string;
  playbookBomKey?: string | null;
  name: string;
  quantity?: number | null;
  unit?: string | null;
  estimatedCost?: number | null;
  currencyCode: string;
  purchaseLink?: string | null;
  notes?: string | null;
  procurementState: "needed" | "obtained" | "not_applicable";
  position: number;
  isTemplateItem: boolean;
  /** 'public' surfaces on Impact tab "What's Needed"; 'team_only' (default)
   *  keeps internal. Migration 166. */
  visibility: "public" | "team_only";
  createdAt: string;
  updatedAt: string;
  // ── Actual-Cost Tracking (Migration 189) ──────────────────────
  /** Actual cost per unit recorded after procurement. Per-unit (parallel
   *  to estimatedCost). NULL until recorded. */
  actualCost?: number | null;
  /** Quantity actually received / procured. NULL until recorded. Falls
   *  back to `quantity` in the server-computed `actualTotal`. */
  actualQuantity?: number | null;
  /** Date of purchase / receipt as an ISO-8601 calendar date string
   *  (YYYY-MM-DD). NULL until recorded. MP-specific; FP preflight_items
   *  has no equivalent. */
  purchaseDate?: string | null;
  /** Computed server-side: estimatedCost × quantity. NULL when either
   *  input is NULL. Read-only on the client. */
  estimatedTotal?: number | null;
  /** Computed server-side: actualCost × (actualQuantity ?? quantity ?? 1).
   *  NULL when actualCost is NULL. Read-only on the client. */
  actualTotal?: number | null;
}

/** Mini-Project enabler — L4E-user address book entry. Per
 *  POLICY_TEAM_ROSTERS.md 2026-05-20 decision, migration 170 retired
 *  role mode; only user-mode entries persist. */
export interface MiniProjectEnablerLink {
  enablerLinkGuid: string;
  miniProjectGuid: string;
  playbookEnablerKey?: string | null;
  targetUserGuid: string;
  topicKey?: string | null;
  topicText?: string | null;
  position: number;
  isTemplateEnabler: boolean;
  createdAt: string;
  updatedAt: string;
}

// ── Goals
//
// DEV-1075 Phase 7.1: the four functions below are DEPRECATED. They
// still work — the backing MiniProjectGoalsController writes to the
// mini_project_goals_archive_2026_05_25 table (renamed by migration 177
// from the original mini_project_goals). Post-Phase-7.1, all goal CRUD
// in the MP UI routes through the unified /api/kpiconfigs endpoints
// instead (KpiConfigResponse + MiniProjectGoalView in
// src/Data/MiniProjectConfig/miniProjectGoalView.ts).
//
// Phase 9 cleanup: the MiniProjectGoalsController, the archive table,
// and these four functions are removed once the unified path has been
// stable for a release cycle. Keep these around in the interim only as
// a safety net in case the unified path needs to be rolled back.
//
// New callers MUST use:
//   fetchKpiConfigsByMiniProject (api/kpiConfigs.ts)
//   createKpiConfig / updateKpiConfig / deactivateKpiConfig (api/kpiConfigs.ts)
//   plus mission_metric_points writes for current value / checklist completion
//   (api/missionMetrics.ts).

/** @deprecated DEV-1075 Phase 7.1 — use fetchKpiConfigsByMiniProject + kpiConfigsToGoalViews. */
export const fetchMiniProjectGoals = (miniProjectGuid: string) =>
  apiFetch<MiniProjectGoal[]>(`/api/mini-projects/${miniProjectGuid}/goals`);

/** @deprecated DEV-1075 Phase 7.1 — use createKpiConfig with miniProjectGuid + scoringMode='qualitative'. */
export const createMiniProjectGoal = (miniProjectGuid: string, req: Partial<MiniProjectGoal> & { primitive: string; nameKey: string }) =>
  apiFetch<MiniProjectGoal>(`/api/mini-projects/${miniProjectGuid}/goals`, { method: "POST", body: JSON.stringify(req) });

/** @deprecated DEV-1075 Phase 7.1 — use updateKpiConfig (goal definition fields) or upsert a mission_metric_point (current_value / checklist completion). */
export const updateMiniProjectGoal = (miniProjectGuid: string, goalGuid: string, req: Partial<MiniProjectGoal>) =>
  apiFetch<MiniProjectGoal>(`/api/mini-projects/${miniProjectGuid}/goals/${goalGuid}`, { method: "PUT", body: JSON.stringify(req) });

/** @deprecated DEV-1075 Phase 7.1 — use deactivateKpiConfig (soft delete via is_active=false). */
export const deleteMiniProjectGoal = (miniProjectGuid: string, goalGuid: string) =>
  apiFetch<void>(`/api/mini-projects/${miniProjectGuid}/goals/${goalGuid}`, { method: "DELETE" });

// ── Tasks
export const fetchMiniProjectTasks = (miniProjectGuid: string) =>
  apiFetch<MiniProjectTask[]>(`/api/mini-projects/${miniProjectGuid}/tasks`);

export const createMiniProjectTask = (miniProjectGuid: string, req: Partial<MiniProjectTask>) =>
  apiFetch<MiniProjectTask>(`/api/mini-projects/${miniProjectGuid}/tasks`, { method: "POST", body: JSON.stringify(req) });

export const updateMiniProjectTask = (miniProjectGuid: string, taskGuid: string, req: Partial<MiniProjectTask>) =>
  apiFetch<MiniProjectTask>(`/api/mini-projects/${miniProjectGuid}/tasks/${taskGuid}`, { method: "PUT", body: JSON.stringify(req) });

export const transitionMiniProjectTask = (miniProjectGuid: string, taskGuid: string, newStatus: "not_started" | "in_progress" | "done") =>
  apiFetch<MiniProjectTask>(`/api/mini-projects/${miniProjectGuid}/tasks/${taskGuid}/transition`, { method: "POST", body: JSON.stringify({ newStatus }) });

export const deleteMiniProjectTask = (miniProjectGuid: string, taskGuid: string) =>
  apiFetch<void>(`/api/mini-projects/${miniProjectGuid}/tasks/${taskGuid}`, { method: "DELETE" });

// ── Team Members
// Migration 172 collapsed the schema (POLICY_TEAM_ROSTERS.md §3). The legacy
// updateMiniProjectTeamMemberRole and roleKey-based addMiniProjectTeamMember
// were retired. Lead promotion goes through transferMiniProjectLead; metadata
// changes go through updateMiniProjectTeamMember (no role field there).
// The bulk-save endpoint is the canonical path for Settings-modal Save flows.

export const fetchMiniProjectTeamMembers = (miniProjectGuid: string, includeFormer = false) =>
  apiFetch<MiniProjectTeamMember[]>(`/api/mini-projects/${miniProjectGuid}/team-members${includeFormer ? "?includeFormer=true" : ""}`);

/* ────────────────────────────────────────────────────────────────
   DEV-1123 (C) — Tasks-in-Engagements.

   GET /api/users/{userGuid}/active-tasks returns all open tasks on this
   user's plate sourced from three places (direct user assignment, held
   role slot, lead-default fallback). Engagements UI renders these in a
   "My Tasks" section parallel to (not folded into) the existing Roles
   section. */

export interface UserActiveTask {
  taskGuid: string;
  miniProjectGuid: string;
  miniProjectTitle: string;
  taskName: string;
  status: "not_started" | "in_progress" | "done";
  dueDate?: string | null;
  /** Why this task is on the user's plate: 'user_assigned' | 'role_assigned' | 'lead_default'. */
  source: string;
  /** Role label when source='role_assigned'. Null otherwise. */
  roleName?: string | null;
  requiresEvidence: boolean;
}

export const fetchUserActiveTasks = (userGuid: string) =>
  apiFetch<UserActiveTask[]>(`/api/users/${userGuid}/active-tasks`);

export const addMiniProjectTeamMember = (miniProjectGuid: string, req: AddMiniProjectTeamMemberRequest) =>
  apiFetch<MiniProjectTeamMember>(`/api/mini-projects/${miniProjectGuid}/team-members`, {
    method: "POST",
    body: JSON.stringify(req),
  });

export const updateMiniProjectTeamMember = (
  miniProjectGuid: string,
  teamMemberGuid: string,
  req: UpdateMiniProjectTeamMemberRequest,
) =>
  apiFetch<MiniProjectTeamMember>(`/api/mini-projects/${miniProjectGuid}/team-members/${teamMemberGuid}`, {
    method: "PUT",
    body: JSON.stringify(req),
  });

export const removeMiniProjectTeamMember = (miniProjectGuid: string, teamMemberGuid: string) =>
  apiFetch<void>(`/api/mini-projects/${miniProjectGuid}/team-members/${teamMemberGuid}`, { method: "DELETE" });

export const transferMiniProjectLead = (miniProjectGuid: string, newLeadUserGuid: string, oldLeadBecomesParticipant = true) =>
  apiFetch<MiniProjectTeamMember>(`/api/mini-projects/${miniProjectGuid}/transfer-lead`, {
    method: "POST",
    body: JSON.stringify({ newLeadUserGuid, oldLeadBecomesParticipant }),
  });

/**
 * Bulk-replace the active team roster with the intended-state list.
 * Server diffs against current rows and applies add/update/remove/
 * transfer-Lead atomically. Used by the Settings modal's Save button
 * so all team edits commit in one transaction.
 */
export const bulkSaveMiniProjectTeamMembers = (
  miniProjectGuid: string,
  members: BulkMiniProjectTeamMember[],
) =>
  apiFetch<MiniProjectTeamMember[]>(`/api/mini-projects/${miniProjectGuid}/team-members/bulk`, {
    method: "PUT",
    body: JSON.stringify({ members }),
  });

// ── BOM Items
export const fetchMiniProjectBomItems = (miniProjectGuid: string) =>
  apiFetch<MiniProjectBomItem[]>(`/api/mini-projects/${miniProjectGuid}/bom-items`);

export const createMiniProjectBomItem = (miniProjectGuid: string, req: Partial<MiniProjectBomItem> & { name: string }) =>
  apiFetch<MiniProjectBomItem>(`/api/mini-projects/${miniProjectGuid}/bom-items`, { method: "POST", body: JSON.stringify(req) });

export const updateMiniProjectBomItem = (
  miniProjectGuid: string,
  bomItemGuid: string,
  // clearFields explicitly nulls optional fields the partial-patch null
  // convention can't (server distinguishes "clear" from "leave unchanged").
  req: Partial<MiniProjectBomItem> & { clearFields?: string[] },
) =>
  apiFetch<MiniProjectBomItem>(`/api/mini-projects/${miniProjectGuid}/bom-items/${bomItemGuid}`, { method: "PUT", body: JSON.stringify(req) });

export const deleteMiniProjectBomItem = (miniProjectGuid: string, bomItemGuid: string) =>
  apiFetch<void>(`/api/mini-projects/${miniProjectGuid}/bom-items/${bomItemGuid}`, { method: "DELETE" });

// ── Enabler Links
export const fetchMiniProjectEnablerLinks = (miniProjectGuid: string) =>
  apiFetch<MiniProjectEnablerLink[]>(`/api/mini-projects/${miniProjectGuid}/enabler-links`);

export const createMiniProjectEnablerLink = (miniProjectGuid: string, req: { targetUserGuid: string; playbookEnablerKey?: string | null; topicKey?: string | null; topicText?: string | null; position?: number }) =>
  apiFetch<MiniProjectEnablerLink>(`/api/mini-projects/${miniProjectGuid}/enabler-links`, { method: "POST", body: JSON.stringify(req) });

export const updateMiniProjectEnablerLink = (miniProjectGuid: string, enablerLinkGuid: string, req: Partial<MiniProjectEnablerLink>) =>
  apiFetch<MiniProjectEnablerLink>(`/api/mini-projects/${miniProjectGuid}/enabler-links/${enablerLinkGuid}`, { method: "PUT", body: JSON.stringify(req) });

export const deleteMiniProjectEnablerLink = (miniProjectGuid: string, enablerLinkGuid: string) =>
  apiFetch<void>(`/api/mini-projects/${miniProjectGuid}/enabler-links/${enablerLinkGuid}`, { method: "DELETE" });

// ── Media: handled by the platform's generic MediaLinks system.
// Use <ProgressGallery entityType="mini_project" entityGuid={mp.miniProjectGuid} canUpload={isTeamMember} />
// — no Mini-Project-specific wrappers needed.
